import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { Building2, Users, Clock, CheckCircle, Plus, ArrowRight } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, accent, sub }) => (
  <div className="bg-white rounded border border-[#E5E5E5] p-5 hover:shadow-md transition-shadow" data-testid={`stat-card-${title}`}>
    <div className="flex items-start justify-between mb-3">
      <div className={`w-10 h-10 rounded flex items-center justify-center flex-shrink-0`} style={{ backgroundColor: accent + '15', color: accent }}>
        <Icon size={18} />
      </div>
      <div className="text-right">
        <div className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B]" data-testid={`stat-value-${title}`}>{value}</div>
      </div>
    </div>
    <div className="text-xs font-bold text-[#555] uppercase tracking-wide">{title}</div>
    {sub && <div className="text-xs text-[#999] mt-0.5">{sub}</div>}
  </div>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/dashboard/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <div className="text-sm text-[#999] animate-pulse">Memuatkan data...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-6xl">

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-xs font-bold text-[#D71920] uppercase tracking-widest mb-1">Portal Pentadbiran</div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#0B0B0B]" data-testid="page-title">Dashboard RSS Admin</h1>
            <p className="text-sm text-[#777] mt-0.5">Gambaran keseluruhan sistem pendaftaran</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => navigate('/admin/schools/new')}
              className="flex items-center gap-2 bg-[#D71920] hover:bg-[#b8161c] text-white text-sm font-semibold px-4 py-2 rounded transition-colors"
              data-testid="register-school-button"
            >
              <Plus size={14} /> Daftar Perguruan
            </button>
            <button
              onClick={() => navigate('/admin/members/new')}
              className="flex items-center gap-2 bg-[#FFD21F] hover:bg-[#f5c800] text-[#0B0B0B] text-sm font-semibold px-4 py-2 rounded transition-colors"
              data-testid="register-member-button"
            >
              <Plus size={14} /> Daftar Ahli
            </button>
          </div>
        </div>

        <div className="h-px bg-[#E5E5E5]" />

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <StatCard title="Jumlah Perguruan" value={stats?.total_schools ?? 0} icon={Building2} accent="#D71920" />
          <StatCard title="Perguruan Pending" value={stats?.pending_schools ?? 0} icon={Clock} accent="#D4AF37" sub="Menunggu kelulusan" />
          <StatCard title="Perguruan Aktif" value={stats?.active_schools ?? 0} icon={CheckCircle} accent="#16a34a" />
          <StatCard title="Jumlah Ahli" value={stats?.total_members ?? 0} icon={Users} accent="#0369A1" />
        </div>

        {/* Quick Access + System Info */}
        <div className="grid sm:grid-cols-2 gap-4">

          {/* Quick Access */}
          <div className="bg-white rounded border border-[#E5E5E5] p-5">
            <div className="text-xs font-bold text-[#555] uppercase tracking-widest mb-4">Akses Pantas</div>
            <div className="space-y-2">
              <button
                onClick={() => navigate('/admin/schools?status=Pending')}
                className="w-full flex items-center justify-between p-4 rounded border border-[#E5E5E5] hover:border-[#D71920]/30 hover:bg-[#FEF2F2]/30 transition-all text-left"
                data-testid="quick-link-pending-schools"
              >
                <div>
                  <p className="text-sm font-semibold text-[#0B0B0B]">Permohonan Pending</p>
                  <p className="text-xs text-[#777] mt-0.5">Lulus atau tolak permohonan perguruan</p>
                </div>
                <div className="flex items-center gap-2">
                  {(stats?.pending_schools ?? 0) > 0 && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#FFD21F] text-[#0B0B0B]">
                      {stats.pending_schools}
                    </span>
                  )}
                  <ArrowRight size={14} className="text-[#D71920]" />
                </div>
              </button>
              <button
                onClick={() => navigate('/admin/members')}
                className="w-full flex items-center justify-between p-4 rounded border border-[#E5E5E5] hover:border-[#D71920]/30 hover:bg-[#FEF2F2]/30 transition-all text-left"
                data-testid="quick-link-all-members"
              >
                <div>
                  <p className="text-sm font-semibold text-[#0B0B0B]">Semua Ahli Silat</p>
                  <p className="text-xs text-[#777] mt-0.5">Carian global ahli seluruh Sarawak</p>
                </div>
                <ArrowRight size={14} className="text-[#D71920]" />
              </button>
              <button
                onClick={() => navigate('/admin/schools')}
                className="w-full flex items-center justify-between p-4 rounded border border-[#E5E5E5] hover:border-[#D71920]/30 hover:bg-[#FEF2F2]/30 transition-all text-left"
                data-testid="quick-link-all-schools"
              >
                <div>
                  <p className="text-sm font-semibold text-[#0B0B0B]">Semua Perguruan</p>
                  <p className="text-xs text-[#777] mt-0.5">Urus perguruan silat Sarawak</p>
                </div>
                <ArrowRight size={14} className="text-[#D71920]" />
              </button>
            </div>
          </div>

          {/* System Info */}
          <div className="bg-white rounded border border-[#E5E5E5] p-5">
            <div className="text-xs font-bold text-[#555] uppercase tracking-widest mb-4">Maklumat Sistem</div>
            <div className="space-y-0">
              {[
                { label: 'Perguruan Aktif', value: stats?.active_schools ?? 0, color: '#16a34a' },
                { label: 'Perguruan Pending', value: stats?.pending_schools ?? 0, color: '#D4AF37' },
                { label: 'Ahli Aktif', value: stats?.active_members ?? 0, color: '#0369A1' },
                { label: 'Jenis Keahlian', value: 'Seumur Hidup', color: '#D71920', text: true },
              ].map((item, i) => (
                <div key={i} className="flex justify-between items-center py-3 border-b border-[#F7F7F7] last:border-0">
                  <span className="text-sm text-[#555]">{item.label}</span>
                  <span
                    className="text-sm font-bold"
                    style={{ color: item.color }}
                    data-testid={`sysinfo-${item.label}`}
                  >
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
